import { ConfigDatabase } from '../../engine/database';
import { itemsData, type ItemConfig } from '../../generated/items';
import { craftsData, type CraftConfig } from '../../generated/crafts';
import { lootPoolsData, type LootPoolConfig } from '../../generated/lootPools';
import { lootPoolEntriesData, type LootPoolEntryConfig } from '../../generated/lootPoolEntries';

export type ItemId = string;
export type CraftId = string;
export type LootPoolId = string;

/**
 * Интерфейс для предмета с дополнительной информацией
 */
export interface ItemInfo extends ItemConfig {
  isResource: boolean;
  isCurrency: boolean;
  isOre: boolean;
  isBar: boolean;
}

/**
 * Интерфейс для рецепта крафта с дополнительной информацией
 */
export interface CraftInfo extends CraftConfig {
  canCraft: boolean;
  missingResources: Array<{ itemId: string; needed: number; available: number }>;
}

/**
 * Интерфейс для записи лута с дополнительной информацией
 */
export interface LootEntryInfo extends LootPoolEntryConfig {
  itemInfo: ItemInfo;
  dropChance: number;
}

/**
 * База данных для игрового контента
 * Объединяет предметы, рецепты крафта и луты
 */
export class GameContentDB {
  private itemsDB: ConfigDatabase<ItemConfig, string>;
  private craftsDB: ConfigDatabase<CraftConfig, string>;
  private lootPoolsDB: ConfigDatabase<LootPoolConfig, string>;
  private lootPoolEntriesDB: ConfigDatabase<LootPoolEntryConfig, string>;

  constructor() {
    this.itemsDB = new ConfigDatabase({
      base: itemsData,
      overrides: {},
      getId: config => config.item_id,
      validate: (config, id) => {
        if (config.base_price < 0) {
          throw new Error(`Item ${id}: base_price must be >= 0`);
        }
        if (config.tier < 0) {
          throw new Error(`Item ${id}: tier must be >= 0`);
        }
      },
    });

    this.craftsDB = new ConfigDatabase({
      base: craftsData,
      overrides: {},
      getId: config => config.recipe_id,
      validate: (config, id) => {
        if (config.base_time_s < 0) {
          throw new Error(`Craft ${id}: base_time_s must be >= 0`);
        }
      },
    });

    this.lootPoolsDB = new ConfigDatabase({
      base: lootPoolsData,
      overrides: {},
      getId: config => config.loot_pool_id,
      validate: (config, id) => {
        if (config.rolls < 0) {
          throw new Error(`LootPool ${id}: rolls must be >= 0`);
        }
      },
    });

    // Создаем Record из массива lootPoolEntriesData
    const lootPoolEntriesRecord: Record<string, LootPoolEntryConfig> = {};
    lootPoolEntriesData.forEach(entry => {
      const id = `${entry.loot_pool_id}-${entry.item_id}`;
      lootPoolEntriesRecord[id] = entry;
    });

    this.lootPoolEntriesDB = new ConfigDatabase({
      base: lootPoolEntriesRecord,
      overrides: {},
      getId: config => `${config.loot_pool_id}-${config.item_id}` as string,
      validate: (config, id) => {
        if (config.weight < 0) {
          throw new Error(`LootPoolEntry ${id}: weight must be >= 0`);
        }
      },
    });
  }

  /**
   * Инициализация всех внутренних баз данных
   */
  init(): void {
    this.itemsDB.init();
    this.craftsDB.init();
    this.lootPoolsDB.init();
    this.lootPoolEntriesDB.init();
  }

  // ===== ITEMS =====

  /**
   * Получить информацию о предмете
   */
  getItemInfo(itemId: ItemId): ItemInfo | undefined {
    const item = this.itemsDB.get(itemId);
    if (!item) return undefined;

    return {
      ...item,
      isResource: item.type === 'resource',
      isCurrency: item.type === 'currency',
      isOre: item.type === 'ore',
      isBar: item.type === 'bar',
    };
  }

  /**
   * Получить все предметы определенного типа
   */
  getItemsByType(type: string): ItemInfo[] {
    return this.itemsDB
      .all()
      .filter(item => item.type === type)
      .map(item => this.getItemInfo(item.item_id)!);
  }

  /**
   * Получить все ресурсы
   */
  getResources(): ItemInfo[] {
    return this.getItemsByType('resource');
  }

  /**
   * Получить все валюты
   */
  getCurrencies(): ItemInfo[] {
    return this.getItemsByType('currency');
  }

  /**
   * Получить все руды
   */
  getOres(): ItemInfo[] {
    return this.getItemsByType('ore');
  }

  /**
   * Получить все сплавы
   */
  getBars(): ItemInfo[] {
    return this.getItemsByType('bar');
  }

  /**
   * Получить предметы по тиру
   */
  getItemsByTier(tier: number): ItemInfo[] {
    return this.itemsDB
      .all()
      .filter(item => item.tier === tier)
      .map(item => this.getItemInfo(item.item_id)!);
  }

  /**
   * Получить предметы по миру
   */
  getItemsByWorld(world: number): ItemInfo[] {
    return this.itemsDB
      .all()
      .filter(item => item.world === world)
      .map(item => this.getItemInfo(item.item_id)!);
  }

  // ===== CRAFTS =====

  /**
   * Получить информацию о рецепте крафта
   */
  getCraftInfo(craftId: CraftId): CraftInfo | undefined {
    const craft = this.craftsDB.get(craftId);
    if (!craft) return undefined;

    // TODO: Интегрировать с системой ресурсов игрока
    const canCraft = true; // Пока всегда true
    const missingResources: Array<{ itemId: string; needed: number; available: number }> = [];

    return {
      ...craft,
      canCraft,
      missingResources,
    };
  }

  /**
   * Получить все рецепты крафта
   */
  getAllCrafts(): CraftInfo[] {
    return this.craftsDB.all().map(craft => this.getCraftInfo(craft.recipe_id)!);
  }

  /**
   * Получить рецепты по тиру
   */
  getCraftsByTier(tier: number): CraftInfo[] {
    return this.craftsDB
      .all()
      .filter(craft => craft.req_tier === tier)
      .map(craft => this.getCraftInfo(craft.recipe_id)!);
  }

  /**
   * Получить рецепты по миру
   */
  getCraftsByWorld(_world: number): CraftInfo[] {
    // Пока нет поля world в CraftConfig, возвращаем все рецепты
    return this.getAllCrafts();
  }

  // ===== LOOT POOLS =====

  /**
   * Получить информацию о пуле лута
   */
  getLootPoolInfo(poolId: LootPoolId): LootPoolConfig | undefined {
    return this.lootPoolsDB.get(poolId);
  }

  /**
   * Получить записи пула лута
   */
  getLootPoolEntries(poolId: LootPoolId): LootEntryInfo[] {
    const entries = this.lootPoolEntriesDB.all().filter(entry => entry.loot_pool_id === poolId);

    const totalWeight = entries.reduce((sum, entry) => sum + entry.weight, 0);

    return entries.map(entry => {
      const itemInfo = this.getItemInfo(entry.item_id);
      return {
        ...entry,
        itemInfo: itemInfo!,
        dropChance: totalWeight > 0 ? entry.weight / totalWeight : 0,
      };
    });
  }

  /**
   * Получить все пулы лута
   */
  getAllLootPools(): LootPoolConfig[] {
    return this.lootPoolsDB.all();
  }

  /**
   * Получить пулы лута по миру
   */
  getLootPoolsByWorld(_world: number): LootPoolConfig[] {
    // Пока нет поля world в LootPoolConfig, возвращаем все пулы
    return this.getAllLootPools();
  }

  // ===== UTILITY METHODS =====

  /**
   * Найти предметы по имени (поиск)
   */
  searchItems(query: string): ItemInfo[] {
    const lowerQuery = query.toLowerCase();
    return this.itemsDB
      .all()
      .filter(
        item =>
          item.name.toLowerCase().includes(lowerQuery) ||
          item.item_id.toLowerCase().includes(lowerQuery)
      )
      .map(item => this.getItemInfo(item.item_id)!);
  }

  /**
   * Получить все ID предметов
   */
  getAllItemIds(): ItemId[] {
    return this.itemsDB.ids() as ItemId[];
  }

  /**
   * Получить все ID рецептов
   */
  getAllCraftIds(): CraftId[] {
    return this.craftsDB.ids() as CraftId[];
  }

  /**
   * Получить все ID пулов лута
   */
  getAllLootPoolIds(): LootPoolId[] {
    return this.lootPoolsDB.ids() as LootPoolId[];
  }

  /**
   * Проверить существование предмета
   */
  hasItem(itemId: ItemId): boolean {
    return this.itemsDB.has(itemId);
  }

  /**
   * Проверить существование рецепта
   */
  hasCraft(craftId: CraftId): boolean {
    return this.craftsDB.has(craftId);
  }

  /**
   * Проверить существование пула лута
   */
  hasLootPool(poolId: LootPoolId): boolean {
    return this.lootPoolsDB.has(poolId);
  }
}

// Ленивый синглтон для удобства
let _gameContentInstance: GameContentDB | null = null;
export function getGameContent(): GameContentDB {
  if (!_gameContentInstance) {
    _gameContentInstance = new GameContentDB();
  }
  return _gameContentInstance;
}

// Синглтон для удобства
export const GameContent = new Proxy({} as GameContentDB, {
  get(_target, prop) {
    return getGameContent()[prop as keyof GameContentDB];
  },
});

// Реэкспортируем типы для удобства
export type { ItemConfig, CraftConfig, LootPoolConfig, LootPoolEntryConfig };
